/**
 * Themes — semantic color palettes.
 *
 * Each theme defines values for the same set of role-named tokens. The runtime
 * sets these as CSS variables on the document root, so swapping themes is a
 * single class change.
 *
 * Free: light, dark
 * Supporter: eight richer board and piece themes unlocked by Ko-fi support
 */

export type ThemeId =
  | 'light'
  | 'dark'
  | 'cinnabar'
  | 'bonewhite'
  | 'oxblood'
  | 'jade'
  | 'stormglass'
  | 'violet'
  | 'obsidian'
  | 'parchment';

export type ThemeKind = 'free' | 'supporter';

// rgb triplets ("r g b") so Tailwind's `rgb(var(--c-x) / <alpha>)` works.
export interface ThemeColors {
  bg: string;
  surface: string;
  surface2: string;
  ink: string;
  inkMuted: string;
  accent: string;
  accent2: string;
  danger: string;
  success: string;
  boardLight: string;
  boardDark: string;
  boardHl: string;
  pieceLight: string;
  pieceDark: string;
  pieceLightStroke: string;
  pieceDarkStroke: string;
  cardRimCommon: string;
  cardRimRare: string;
  cardRimLegendary: string;
}

export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  kind: ThemeKind;
  scheme: 'light' | 'dark';
  colors: ThemeColors;
  /** Short one-line vibe text used in the theme picker. */
  blurb: string;
}

export const THEMES: Record<ThemeId, ThemeDefinition> = {
  light: {
    id: 'light',
    name: 'Light',
    kind: 'free',
    scheme: 'light',
    blurb: 'Clean light board for daytime play.',
    colors: {
      bg: '250 248 244',
      surface: '255 253 249',
      surface2: '244 240 232',
      ink: '26 20 16',
      inkMuted: '95 84 70',
      accent: '184 99 41',
      accent2: '138 60 19',
      danger: '168 53 53',
      success: '76 122 56',
      boardLight: '232 213 168',
      boardDark: '138 96 56',
      boardHl: '244 196 64',
      pieceLight: '252 248 238',
      pieceDark: '36 26 22',
      pieceLightStroke: '36 26 22',
      pieceDarkStroke: '248 240 220',
      cardRimCommon: '170 156 138',
      cardRimRare: '76 110 168',
      cardRimLegendary: '184 99 41',
    },
  },
  dark: {
    id: 'dark',
    name: 'Dark',
    kind: 'free',
    scheme: 'dark',
    blurb: 'Low-glare dark board for night games.',
    colors: {
      bg: '18 14 12',
      surface: '28 22 18',
      surface2: '40 32 26',
      ink: '240 232 220',
      inkMuted: '168 156 140',
      accent: '224 142 76',
      accent2: '246 184 124',
      danger: '220 90 90',
      success: '130 184 96',
      boardLight: '200 168 124',
      boardDark: '86 56 30',
      boardHl: '240 168 60',
      pieceLight: '248 238 218',
      pieceDark: '32 24 22',
      pieceLightStroke: '32 24 22',
      pieceDarkStroke: '246 232 206',
      cardRimCommon: '120 108 92',
      cardRimRare: '120 156 220',
      cardRimLegendary: '224 142 76',
    },
  },
  cinnabar: {
    id: 'cinnabar',
    name: 'Cinnabar',
    kind: 'supporter',
    scheme: 'dark',
    blurb: 'Vermillion lacquer and brass. Aggressive.',
    colors: {
      bg: '22 12 12',
      surface: '36 18 18',
      surface2: '52 26 24',
      ink: '244 232 216',
      inkMuted: '188 156 140',
      accent: '232 76 56',
      accent2: '255 124 96',
      danger: '236 100 100',
      success: '140 196 108',
      boardLight: '220 168 132',
      boardDark: '104 40 32',
      boardHl: '255 184 76',
      pieceLight: '255 238 218',
      pieceDark: '44 18 18',
      pieceLightStroke: '54 20 18',
      pieceDarkStroke: '255 228 204',
      cardRimCommon: '136 96 84',
      cardRimRare: '160 156 220',
      cardRimLegendary: '255 124 96',
    },
  },
  bonewhite: {
    id: 'bonewhite',
    name: 'Bonewhite',
    kind: 'supporter',
    scheme: 'light',
    blurb: 'Carved bone, sepia, and forest ink.',
    colors: {
      bg: '244 240 230',
      surface: '252 248 240',
      surface2: '236 228 212',
      ink: '32 28 22',
      inkMuted: '88 80 64',
      accent: '88 124 56',
      accent2: '52 86 36',
      danger: '156 56 46',
      success: '88 124 56',
      boardLight: '224 210 176',
      boardDark: '118 96 64',
      boardHl: '212 168 72',
      pieceLight: '255 252 238',
      pieceDark: '34 46 34',
      pieceLightStroke: '36 32 24',
      pieceDarkStroke: '238 230 204',
      cardRimCommon: '168 158 130',
      cardRimRare: '92 116 156',
      cardRimLegendary: '88 124 56',
    },
  },
  oxblood: {
    id: 'oxblood',
    name: 'Oxblood',
    kind: 'supporter',
    scheme: 'dark',
    blurb: 'Burgundy leather and gilt. Old club.',
    colors: {
      bg: '20 12 14',
      surface: '32 18 22',
      surface2: '48 26 30',
      ink: '236 224 208',
      inkMuted: '180 156 144',
      accent: '196 154 70',
      accent2: '232 192 110',
      danger: '212 92 92',
      success: '132 176 92',
      boardLight: '208 176 132',
      boardDark: '88 44 44',
      boardHl: '232 192 110',
      pieceLight: '246 232 210',
      pieceDark: '44 20 28',
      pieceLightStroke: '60 28 30',
      pieceDarkStroke: '236 214 184',
      cardRimCommon: '128 108 96',
      cardRimRare: '140 152 200',
      cardRimLegendary: '232 192 110',
    },
  },
  jade: {
    id: 'jade',
    name: 'Jade Court',
    kind: 'supporter',
    scheme: 'dark',
    blurb: 'Deep green lacquer with pale stone pieces.',
    colors: {
      bg: '10 24 22',
      surface: '18 38 34',
      surface2: '26 52 46',
      ink: '232 244 226',
      inkMuted: '158 188 168',
      accent: '94 190 138',
      accent2: '150 220 170',
      danger: '224 94 94',
      success: '116 212 146',
      boardLight: '176 212 174',
      boardDark: '42 100 78',
      boardHl: '232 204 104',
      pieceLight: '238 246 222',
      pieceDark: '12 42 36',
      pieceLightStroke: '18 46 38',
      pieceDarkStroke: '220 240 212',
      cardRimCommon: '86 124 106',
      cardRimRare: '104 168 204',
      cardRimLegendary: '150 220 170',
    },
  },
  stormglass: {
    id: 'stormglass',
    name: 'Stormglass',
    kind: 'supporter',
    scheme: 'dark',
    blurb: 'Cool glass, rain light, and brass markers.',
    colors: {
      bg: '14 18 24',
      surface: '24 30 38',
      surface2: '34 42 52',
      ink: '236 242 246',
      inkMuted: '164 178 190',
      accent: '214 162 78',
      accent2: '236 194 112',
      danger: '226 96 96',
      success: '104 190 150',
      boardLight: '178 196 202',
      boardDark: '64 82 96',
      boardHl: '236 190 92',
      pieceLight: '246 250 250',
      pieceDark: '28 36 48',
      pieceLightStroke: '26 34 44',
      pieceDarkStroke: '230 240 242',
      cardRimCommon: '112 128 140',
      cardRimRare: '92 162 204',
      cardRimLegendary: '236 194 112',
    },
  },
  violet: {
    id: 'violet',
    name: 'Royal Violet',
    kind: 'supporter',
    scheme: 'dark',
    blurb: 'Velvet violet, ivory pieces, gold calls.',
    colors: {
      bg: '22 16 32',
      surface: '34 24 48',
      surface2: '48 34 66',
      ink: '244 236 226',
      inkMuted: '190 174 200',
      accent: '222 178 88',
      accent2: '248 212 126',
      danger: '226 96 120',
      success: '132 196 132',
      boardLight: '206 186 218',
      boardDark: '82 54 118',
      boardHl: '244 202 106',
      pieceLight: '252 240 222',
      pieceDark: '42 26 58',
      pieceLightStroke: '44 28 58',
      pieceDarkStroke: '242 226 208',
      cardRimCommon: '132 112 148',
      cardRimRare: '128 164 220',
      cardRimLegendary: '248 212 126',
    },
  },
  obsidian: {
    id: 'obsidian',
    name: 'Obsidian',
    kind: 'supporter',
    scheme: 'dark',
    blurb: 'Deep black, slate, and electric blue.',
    colors: {
      bg: '10 12 16',
      surface: '18 22 28',
      surface2: '28 34 42',
      ink: '232 240 248',
      inkMuted: '148 162 180',
      accent: '88 168 255',
      accent2: '136 196 255',
      danger: '232 88 102',
      success: '108 196 156',
      boardLight: '116 132 156',
      boardDark: '36 44 58',
      boardHl: '88 168 255',
      pieceLight: '236 244 252',
      pieceDark: '14 18 26',
      pieceLightStroke: '20 26 36',
      pieceDarkStroke: '212 224 240',
      cardRimCommon: '108 120 138',
      cardRimRare: '88 168 255',
      cardRimLegendary: '180 220 255',
    },
  },
  parchment: {
    id: 'parchment',
    name: 'Parchment',
    kind: 'supporter',
    scheme: 'light',
    blurb: 'Warm cream paper, sepia ink, and ochre.',
    colors: {
      bg: '246 238 220',
      surface: '252 246 230',
      surface2: '236 224 198',
      ink: '52 36 22',
      inkMuted: '116 96 72',
      accent: '188 124 56',
      accent2: '156 92 36',
      danger: '176 72 56',
      success: '116 134 68',
      boardLight: '236 218 178',
      boardDark: '158 116 70',
      boardHl: '226 168 80',
      pieceLight: '252 244 222',
      pieceDark: '54 38 22',
      pieceLightStroke: '54 38 22',
      pieceDarkStroke: '244 232 204',
      cardRimCommon: '164 144 110',
      cardRimRare: '120 132 168',
      cardRimLegendary: '188 124 56',
    },
  },
} as const;

export const FREE_THEMES: ThemeId[] = ['light', 'dark'];
export const SUPPORTER_THEMES: ThemeId[] = ['cinnabar', 'bonewhite', 'oxblood', 'jade', 'stormglass', 'violet', 'obsidian', 'parchment'];

export function cssVarsForTheme(theme: ThemeDefinition): Record<string, string> {
  const c = theme.colors;
  return {
    '--c-bg': c.bg,
    '--c-surface': c.surface,
    '--c-surface-2': c.surface2,
    '--c-ink': c.ink,
    '--c-ink-muted': c.inkMuted,
    '--c-accent': c.accent,
    '--c-accent-2': c.accent2,
    '--c-danger': c.danger,
    '--c-success': c.success,
    '--c-board-light': c.boardLight,
    '--c-board-dark': c.boardDark,
    '--c-board-hl': c.boardHl,
    '--c-piece-light': c.pieceLight,
    '--c-piece-dark': c.pieceDark,
    '--c-piece-light-stroke': c.pieceLightStroke,
    '--c-piece-dark-stroke': c.pieceDarkStroke,
    '--c-card-rim-common': c.cardRimCommon,
    '--c-card-rim-rare': c.cardRimRare,
    '--c-card-rim-legendary': c.cardRimLegendary,
  };
}

export function themeAsCssText(theme: ThemeDefinition): string {
  const vars = cssVarsForTheme(theme);
  return Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
}
